import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Avatar,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Chip,
  Alert,
  Divider
} from '@mui/material';
import {
  SwapHoriz as SwapHorizIcon,
  WorkOutline as WorkOutlineIcon
} from '@mui/icons-material';
import moment from 'moment';
import { Employee, Schedule } from '../../../pages/Dashboard/Dashboard';

interface Shift {
  shiftName: string;
  shiftId: string;
  notes: string;
  assignedStaff: string[];
  staffNeeded: string;
  startTime: string; // "HH:mm" format
  endTime: string; // "HH:mm" format
  position: string; // position ID
  isRecurring: boolean;
  daysOfWeek?: string[];
  scheduleId: string;
  shiftDate?: string; // For non-recurring shifts
}

interface SwapShiftModalProps {
  open: boolean;
  onClose: () => void;
  shift?: Shift | null;
  currentEmployeeId: string;
  employees: Employee[];
  positions: Record<string, any>[];
  availableShifts?: Shift[]; // List of shifts user can swap from
  onSubmitSwapRequest?: (shiftId: string, targetEmployeeId: string, reason: string, isPermanent: boolean, swapDate: string) => void;
  selectedSchedule: Schedule | null;
  selectedDate: string | null;
}

const SwapShiftModal: React.FC<SwapShiftModalProps> = ({
  open,
  onClose,
  shift,
  currentEmployeeId,
  employees,
  positions,
  availableShifts = [],
  onSubmitSwapRequest,
  selectedSchedule,
  selectedDate
}) => {
  const [selectedShiftId, setSelectedShiftId] = useState('');
  const [swapTargetEmployee, setSwapTargetEmployee] = useState('');
  const [swapReason, setSwapReason] = useState('');
  const [isPermanent, setIsPermanent] = useState(false);

  React.useEffect(() => {
    console.debug('swapTargetEmployee changed ->', swapTargetEmployee);
  }, [swapTargetEmployee]);

  // Determine the current shift to work with
  const currentShift = shift || availableShifts.find(s => s.shiftId === selectedShiftId);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      if (shift) {
        // If a specific shift is provided, use it
        setSelectedShiftId(shift.shiftId);
      } else {
        // Reset everything for shift selection mode
        setSelectedShiftId('');
      }
      setSwapTargetEmployee('');
      setSwapReason('');
      setIsPermanent(false);
    }
  }, [open, shift]);

  // Helper function to get position by ID
  const getPosition = (positionId: string) => {
    return positions.find(p => p.position === positionId);
  };

  const formatShiftTime = (timeStr: string) => {
    const [hour, min] = timeStr.split(':').map(Number);
    return moment().hours(hour).minutes(min).format('h:mm A');
  };

  // Get user's shifts (only shifts they're assigned to)
  const userShifts = availableShifts.filter(s =>
    s.assignedStaff.includes(currentEmployeeId)
  );

  // Get available employees for swapping (exclude current user and those already on the selected shift)
  const availableForSwap = currentShift
    ? employees.filter(emp =>
      emp.userId !== currentEmployeeId &&
      !currentShift.assignedStaff.includes(emp.userId) &&
      positions.find(p => p.position === currentShift.position)?.employees?.includes(emp.userId)
    )
    : [];

  const handleClose = () => {
    setSelectedShiftId('');
    setSwapTargetEmployee('');
    setSwapReason('');
    setIsPermanent(false);
    onClose();
  };

  const handleSubmitSwapRequest = () => {
    if (currentShift && swapTargetEmployee && onSubmitSwapRequest) {
      onSubmitSwapRequest(currentShift.shiftId, swapTargetEmployee, swapReason, isPermanent, selectedDate || moment().format("YYYY-MM-DD"));
      handleClose();
    }
  };

  const getShiftDisplayText = (shiftData: Shift) => {
    const timeText = `${formatShiftTime(shiftData.startTime)} - ${formatShiftTime(shiftData.endTime)}`;

    if (shiftData.isRecurring && shiftData.daysOfWeek) {
      const days = shiftData.daysOfWeek.map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(', ');
      return `${shiftData.shiftName} • ${timeText} • ${days}`;
    } else if (shiftData.shiftDate) {
      const dateText = moment(shiftData.shiftDate).format('MMM D, YYYY');
      return `${shiftData.shiftName} • ${timeText} • ${dateText}`;
    } else {
      return `${shiftData.shiftName} • ${timeText}`;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <SwapHorizIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Request Shift Swap
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {shift ? 'Choose who you\'d like to swap with' : 'Select a shift to swap and choose a coworker'}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Shift Selection (only show if no specific shift was provided) */}
        {!shift && (
          <>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Select Your Shift to Swap</InputLabel>
              <Select
                value={selectedShiftId}
                label="Select Your Shift to Swap"
                onChange={(e) => setSelectedShiftId(e.target.value)}
              >
                {userShifts.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      No shifts available for swapping
                    </Typography>
                  </MenuItem>
                ) : (
                  userShifts.map(shiftData => (
                    <MenuItem key={shiftData.shiftId} value={shiftData.shiftId}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5, width: '100%' }}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem', bgcolor: 'secondary.main' }}>
                          <WorkOutlineIcon sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={500} noWrap>
                            {shiftData.shiftName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {getShiftDisplayText(shiftData)}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
              {userShifts.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, ml: 1 }}>
                  You need to be assigned to shifts before you can request swaps.
                </Typography>
              )}
            </FormControl>

            {selectedShiftId && <Divider sx={{ mb: 3 }} />}
          </>
        )}

        {/* Current Shift Display */}
        {currentShift && (
          <>
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                {shift ? 'Your Shift to Swap' : 'Selected Shift to Swap'}
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {currentShift.shiftName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {formatShiftTime(currentShift.startTime)} - {formatShiftTime(currentShift.endTime)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={getPosition(currentShift.position)?.positionName || getPosition(currentShift.position)?.name || 'Unknown Position'}
                  size="small"
                />
                {currentShift.isRecurring && (
                  <Chip
                    label="Recurring"
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                )}
                {!currentShift.isRecurring && currentShift.shiftDate && (
                  <Chip
                    label={moment(currentShift.shiftDate).format('MMM D')}
                    size="small"
                    color="default"
                    variant="outlined"
                  />
                )}
              </Box>

              {/* Show recurring days */}
              {currentShift.isRecurring && currentShift.daysOfWeek && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Repeats on:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    {currentShift.daysOfWeek.map(day => (
                      <Chip
                        key={day}
                        label={day.charAt(0).toUpperCase() + day.slice(1, 3)}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 24 }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Show notes if any */}
              {currentShift.notes && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                  Note: {currentShift.notes}
                </Typography>
              )}
            </Paper>

            {/* Employee Selection */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Swap With Staff</InputLabel>
              <Select
                value={swapTargetEmployee}
                label="Swap With Staff"
                onChange={(e) => { setSwapTargetEmployee(e.target.value) }}
                disabled={!currentShift}
              >
                <MenuItem key={'any'} value={'any'} onClick={() => { setSwapTargetEmployee('any'); }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem', bgcolor: 'primary.main' }}>
                      A
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>Any Available Coworker</Typography>
                    </Box>
                  </Box>
                </MenuItem>
                {(availableForSwap.map(emp => (
                  <MenuItem key={emp.userId} value={emp.userId} onClick={() => { setSwapTargetEmployee(emp.userId); }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem', bgcolor: 'primary.main' }}>
                        {emp.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{emp.name}</Typography>
                      </Box>
                    </Box>
                  </MenuItem>))
                )}
              </Select>
              {availableForSwap.length === 0 && currentShift && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, ml: 1 }}>
                  All other staff are either already assigned to this shift or unavailable.
                </Typography>
              )}
            </FormControl>

            {/* Reason TextField */}
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Reason for Swap (Optional)"
              placeholder="Why do you need to swap this shift?"
              value={swapReason}
              onChange={(e) => setSwapReason(e.target.value)}
              disabled={!currentShift}
            />

            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Swap duration
              </Typography>
              <RadioGroup row value={isPermanent ? 'forever' : 'today'} onChange={(e) => setIsPermanent(e.target.value === 'forever')}>
                <FormControlLabel value="today" control={<Radio />} label={"Today only " + moment().format("(MMM/DD)")} disabled={!currentShift} />
                <FormControlLabel value="forever" control={<Radio />} label="All shifts moving forward" disabled={!currentShift} />
              </RadioGroup>
            </FormControl>

            {!selectedSchedule?.allowSwapWithoutApproval ?
              <Alert severity="info" sx={{ mt: 2 }}>
                Your request will first be sent to your manager for approval. 
                Then the selected staff member will receive a notification about your swap request. They can accept or decline.
              </Alert>
              : <Alert severity="info" sx={{ mt: 2 }}>
              The selected staff member will receive a notification about your swap request. They can accept or decline.
            </Alert>}
          </>
        )}

        {/* Show message when no shift is selected */}
        {!currentShift && !shift && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: 'grey.200', mx: 'auto', mb: 2 }}>
              <WorkOutlineIcon sx={{ fontSize: 32, color: 'grey.400' }} />
            </Avatar>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Select a shift to get started
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Choose one of your assigned shifts to request a swap with a coworker
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmitSwapRequest}
          variant="contained"
          disabled={!currentShift || !swapTargetEmployee}
          startIcon={<SwapHorizIcon />}
        >
          Request Swap
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SwapShiftModal;
